/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { notification } from "@duxton-web/ui"

import {
  RETURN_NATIVE_HANDLER,
  RETURN_NATIVE_TOPIC,
  RETURN_REFUND_TEXT,
} from "ecommerce/constants/returnRefund"
import { callFlutterBridge, NativeHandlerArgs } from "utils/nativeBridge"
import { isWebview } from "utils/webview"

/**
 * Payload each handler expects, built here rather than at the call sites so a trigger point
 * cannot pass the wrong shape. The app's `NavigationHandler` reads these by key and falls
 * back to `args.deeplink` for anything it does not map — so a wrong or missing key navigates
 * nowhere instead of erroring, which is exactly the failure that is hard to spot inside a
 * webview.
 *
 * Note the app's spelling: `orderId`, not the `orderID` used everywhere on the web side.
 */
const withOrderID = (orderID?: string): NativeHandlerArgs =>
  /*
   * Omit the key rather than sending `orderId: undefined`. A present-but-undefined key
   * still counts toward `Object.keys().length`, so it would slip past the empty-payload
   * check in `notifyNativeHandler` and then be dropped when the bridge serialises the
   * payload — leaving the app with a key it needs, absent, and no error to show for it.
   */
  orderID === undefined ? {} : { orderId: orderID }

const buildHandlerArgs = (
  handler: string,
  orderID?: string,
  roomID?: string,
): NativeHandlerArgs => {
  switch (handler) {
    case RETURN_NATIVE_HANDLER.trackHere:
      return withOrderID(orderID)
    /*
     * Opens the app's chat screen, and the room IS the scope: the app addresses the
     * exact buyer conversation the clarify decision opened, so no context/order keys
     * ride along. A call without a room (the header's plain chat entry, or a response
     * that carried none) sends it empty — the app owns what an unaddressed chat
     * opens, and the follow-up chat MR gives that entry a real room.
     */
    case RETURN_NATIVE_HANDLER.chat:
      return { roomID: roomID ?? "" }
    case RETURN_NATIVE_HANDLER.raiseIssue:
      return { topic: RETURN_NATIVE_TOPIC }
    /*
     * Registered by the app with no parameters, so the payload is always empty — pinned
     * here rather than left to `default`, which would send an `orderId` the handler was
     * never written to receive if a call site ever passed one.
     */
    case RETURN_NATIVE_HANDLER.closeWebview:
      return {}
    /*
     * In the app's handler table but not its deeplink switch, so the app owns the
     * behaviour — a bottom sheet. `orderId` is sent because it acts on the request being
     * viewed, and the switch's default branch ignores keys it does not know.
     */
    case RETURN_NATIVE_HANDLER.option:
      return withOrderID(orderID)
    default:
      return withOrderID(orderID)
  }
}

/*
 * Merchant-facing copy is deliberately generic. The handler name is an internal
 * implementation detail — naming it in a toast tells the merchant nothing they can act on,
 * so it goes to the console for whoever is debugging the webview instead.
 *
 * The string lives in `RETURN_REFUND_TEXT` with the rest of the feature's copy, so it is
 * covered by the same placeholder-to-PhraseApp swap rather than being a literal this
 * module owns privately.
 */
const reportUnavailable = (reason: string, handler?: string): void => {
  // eslint-disable-next-line no-console
  console.warn(`notifyNativeHandler: ${reason}`, { handler })
  notification.info({ message: RETURN_REFUND_TEXT.nativeActionUnavailable })
}

/**
 * Hands an action to the native app.
 *
 * Gated on context, because these callbacks only exist in the app. On desktop the same
 * triggers need a web destination instead — a Help Centre page, the tracker URL — and those
 * are still unknown (Q28), so it says so rather than calling a handler that could never fire
 * there.
 *
 * A missing bridge global inside a webview is treated the same way: it means the app never
 * registered its handlers, and doing nothing silently would look like a dead button.
 */
export const notifyNativeHandler = (
  handler?: string,
  orderID?: string,
  /** Message Center room, for chat handlers once the server has opened one. */
  roomID?: string,
): void => {
  if (!isWebview()) {
    reportUnavailable("no web destination wired for this action", handler)
    return
  }

  if (!handler) {
    reportUnavailable("no native handler defined for this action")
    return
  }

  /*
   * An empty payload is sent as no payload at all. `callFlutterBridge` deliberately omits
   * the argument when none is supplied, so a handler the app registered with zero
   * parameters — `closeWebview` — sees the call shape it was written for. Passing `{}`
   * would hand it an argument it does not expect.
   */
  const args = buildHandlerArgs(handler, orderID, roomID)
  const pending = callFlutterBridge(
    handler,
    Object.keys(args).length > 0 ? args : undefined,
  )
  if (!pending) {
    reportUnavailable("handler is not registered by the app", handler)
    return
  }

  /*
   * These handlers navigate; nothing comes back worth rendering. A rejection still needs
   * catching so it does not surface as an unhandled rejection in the app's console.
   */
  pending.catch(() => {
    reportUnavailable("handler rejected", handler)
  })
}
